import React, { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PageLayout } from "../../ui-components/PageLayout";
import {
  Box,
  Grid,
  Typography,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  Divider,
  Stack,
  Chip,
  Link as MuiLink,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  getTransactionById,
  getTransactionNavigation,
} from "../../store/modules/transactions/transactionActions";
import LoadingDialog from "../../ui-components/Dialogs/LoadingDialog";
import { RootState } from "../../store/reducers";
import { ITransactionInfoModel } from "../../types/types";
import { useQuery } from "@tanstack/react-query";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import TagIcon from "@mui/icons-material/Tag";
import NoteIcon from "@mui/icons-material/Note";
import { formatDate } from "../../utils/common";
import { getUserCustomData } from "../../store/modules/common/commonActions";
import { AppDispatch } from "../../store/store";
import { Dayjs } from "dayjs";
import {
  PIPELINE_ROUTE,
  closingDateLabel,
  isOverdueEntry,
  isPipelineStatus,
  transactionStatusLabel,
} from "../../hooks/usePipeline";

interface InfoFieldProps {
  label: string;
  value: string | number | Array<{ name?: string } | string> | undefined;
  icon?: React.ReactNode;
  chip?: boolean;
}

const DateField: React.FC<{ label: string; value: Dayjs | undefined }> = ({
  label,
  value,
}) => {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={{ color: "primary.main", mt: 0.5 }}>
        <CalendarTodayIcon fontSize="small" />
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {label}
        </Typography>

        <Typography variant="body1">
          {value?.format("MM/DD/YYYY") ?? "N/A"}
        </Typography>
      </Box>
    </Stack>
  );
};

const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  icon,
  chip = false,
}) => {
  if (value !== "" && !value && value !== 0) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
      {icon && <Box sx={{ color: "primary.main", mt: 0.5 }}>{icon}</Box>}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {label}
        </Typography>
        {chip ? (
          <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {Array.isArray(value) &&
              value.map((item: { name?: string } | string, index: number) => (
                <Chip
                  key={index}
                  label={
                    typeof item === "object" && item !== null
                      ? item.name || ""
                      : item
                  }
                  size="small"
                  sx={{ mb: 0.5 }}
                />
              ))}
          </Box>
        ) : (
          <Typography variant="body1">{String(value)}</Typography>
        )}
      </Box>
    </Stack>
  );
};

interface FinancialFieldProps {
  label: string;
  value: number | string | null | undefined;
  percentage?: boolean;
}

const FinancialField: React.FC<FinancialFieldProps> = ({
  label,
  value,
  percentage = false,
}) => {
  // if (value === null || value === undefined) return null;

  const displayValue =
    value === null || value === undefined
      ? "N/A"
      : percentage
        ? `${Number(value).toFixed(2)}%`
        : `$${Number(value).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          fontWeight={label.includes("Net") ? "medium" : "regular"}
        >
          {displayValue}
        </Typography>
      </Box>
    </Grid>
  );
};

export default function TransactionView() {
  const { transactionId } = useParams<{ transactionId: string }>();
  const [year, setYear] = useState<number | undefined>();
  const [isNavigating, setIsNavigating] = useState(false);
  const [searchParams] = useSearchParams();
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const [activeTab, setActiveTab] = useState<string>("main");
  const [areaName, setAreaName] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the Add page after creating an entry
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { successMessage?: string } | null)?.successMessage ??
      null,
  );
  useEffect(() => {
    if ((location.state as { successMessage?: string } | null)?.successMessage) {
      // Drop it from history so a refresh or Back doesn't show it again
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
    }
  }, [location, navigate]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "financials") {
      setActiveTab("financials");
    }
  }, [searchParams]);
  const {
    data: result,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["transaction", transactionId],
    queryFn: async () => {
      if (!transactionId) return;
      try {
        return await getTransactionById(Number(transactionId));
      } catch (err) {
        throw new Error("Failed to load transaction details");
      }
    },
    enabled: !!transactionId,
    refetchOnWindowFocus: false,
  });

  const transaction = result?.transactionDetail as ITransactionInfoModel;
  // Folder of the loaded entry. Read from the query data (not state set while fetching), so after
  // an arrow click the navigation never runs with the previous entry's folder
  const selectedYearId = result?.transactionDetail?.yearId ?? undefined;
  useEffect(() => {
    const currentYear = years.find((x) => x.id === selectedYearId)?.year;
    setYear(currentYear);
  }, [selectedYearId, years]);
  const status = result?.transactionDetail?.status;
  const leadReceivedDate = result?.transactionDetail?.leadReceivedDate;
  const isPipelineEntry = isPipelineStatus(status);
  const isOverdue = isOverdueEntry(status, transaction?.date);
  // const { data: prefillData } = useQuery({
  //   queryKey: ["fetchFinancialDefaultData", transactionId, transaction?.date],
  //   refetchOnWindowFocus: false,
  //   queryFn: () =>
  //     getFinancialDefaultData(Number(transactionId), transaction?.date),
  //   enabled: !!transactionId && !!transaction,
  // });
  const { data: navigationData, isLoading: navigationLoading } = useQuery({
    queryKey: ["transactionNavigation", transactionId, selectedYearId],
    queryFn: async () => {
      if (!transactionId || !selectedYearId) return null;
      try {
        const result = await getTransactionNavigation(
          Number(transactionId),
          selectedYearId,
        );
        return result;
      } catch (err) {
        console.error("Failed to load transaction navigation:", err);
        return null;
      }
    },
    enabled: !!transactionId && !!selectedYearId,
    refetchOnWindowFocus: false,
  });

  const handleNavigation = async (isNext: boolean) => {
    if (!transactionId || !selectedYearId || isNavigating) return;

    const targetTransactionId = isNext
      ? navigationData?.nextTransactionId
      : navigationData?.previousTransactionId;

    if (!targetTransactionId) return;

    setIsNavigating(true);
    try {
      navigate(`/transactions/view/${targetTransactionId}`);
    } catch (error) {
      console.error(
        `Failed to navigate to ${isNext ? "next" : "previous"} transaction:`,
        error,
      );
    } finally {
      setIsNavigating(false);
    }
  };

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const getAreaName = () => {
      if (!transaction?.areaId || !userData?.areas) return "";
      const area = userData.areas.find((a) => a.id === transaction.areaId);
      return area?.name || "";
    };

    if (transaction && userData?.areas) setAreaName(getAreaName());
  }, [transaction, userData]);

  // useQuery({
  //   queryKey: ["getUserCustomData"],
  //   queryFn: () => {
  //     dispatch(getUserCustomData());
  //     return Promise.resolve(null);
  //   },
  //   refetchOnWindowFocus: false,
  //   enabled:
  //     !!transaction &&
  //     ((!!transaction.areaId &&
  //       !userData?.areas?.some((a) => a.id === transaction.areaId)) ||
  //       (transaction.specialGroups &&
  //         transaction.specialGroups!.length > 0 &&
  //         !transaction.specialGroups!.every((id) =>
  //           userData?.specialGroups?.some((g) => g.id === id)
  //         )) ||
  //       (transaction.specialTags &&
  //         transaction.specialTags!.length > 0 &&
  //         !transaction.specialTags!.every((id) =>
  //           userData?.specialTags?.some((t) => t.id === id)
  //         ))),
  // });
  if (queryError)
    return (
      <Typography color="error">
        Error loading transaction: {queryError.message}
      </Typography>
    );

  const getTransactionTypeName = () => {
    if (!transaction?.typeId || !userData?.transactionTypes) return "";
    const type = userData.transactionTypes.find(
      (t) => t.id === transaction.typeId,
    );
    return type?.name || "";
  };

  const getIsReferralOut = (): boolean => {
    if (!transaction?.typeId || !userData?.transactionTypes) return false;
    const type = userData.transactionTypes.find(
      (t) => t.id === transaction.typeId,
    );
    return type?.showReferralOut ?? false;
  };

  const getReferralOutTypeName = () => {
    if (!transaction?.referralOutTypeId || !userData?.referralOutTypes)
      return "";
    const type = userData.referralOutTypes.find(
      (t) => t.id === transaction.referralOutTypeId,
    );
    return type?.name || "";
  };

  const getSourceName = () => {
    if (!transaction?.sourceId || !userData?.sources) return "";
    const source = userData.sources.find((s) => s.id === transaction.sourceId);
    return source?.name || "";
  };

  const getReasonName = () => {
    if (!transaction?.reasonId || !userData?.reasons) return "";
    const reason = userData.reasons.find((r) => r.id === transaction.reasonId);
    return reason?.name || "";
  };

  const getCoAgentName = () => {
    if (!transaction?.coAgentId || !userData?.coAgents) return "";
    const coAgent = userData.coAgents.find(
      (c) => c.id === transaction.coAgentId,
    );
    return coAgent?.name || "";
  };

  const getCoAgent2Name = () => {
    if (!transaction?.coAgent2Id || !userData?.coAgents) return "";
    const coAgent2 = userData.coAgents.find(
      (c) => c.id === transaction.coAgent2Id,
    );
    return coAgent2?.name || "";
  };

  const getSpecialGroups = (): string[] => {
    if (!transaction?.specialGroups || !userData?.specialGroups) return [];
    return transaction.specialGroups
      .map((id) => {
        const group = userData?.specialGroups?.find((g) => g.id === id);
        return group?.name || "";
      })
      .filter((name) => name);
  };

  const getSpecialTags = (): string[] => {
    if (!transaction?.specialTags || !userData?.specialTags) return [];
    return transaction.specialTags
      .map((id) => {
        const tag = userData?.specialTags?.find((t) => t.id === id);
        return tag?.name || "";
      })
      .filter((name) => name);
  };

  return (
    <PageLayout>
      <LoadingDialog open={isLoading || isNavigating} />
      <Snackbar
        open={successMessage !== null}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessMessage(null)}
          variant="filled"
          elevation={6}
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ mb: 6, mx: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <MuiLink
              component={Link}
              to="/dashboard"
              color="inherit"
              underline="hover"
            >
              Dashboard
            </MuiLink>
            <MuiLink
              component={Link}
              to={`/transactions/${isPipelineEntry ? PIPELINE_ROUTE : (year ?? "")}`}
              color="inherit"
              underline="hover"
            >
              {isPipelineEntry ? "Pipeline" : `Transactions for ${year}`}
            </MuiLink>
            <Typography color="text.primary">View</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header with Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="h4"
              color="primary"
              component="h1"
              fontWeight="bold"
            >
              Transaction
            </Typography>

            <Button
              variant="contained"
              component={Link}
              to={`/transactions/edit/${transactionId}${
                activeTab === "financials" ? "?tab=financials" : ""
              }`}
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
              }}
            >
              Edit
            </Button>
          </Box>

          {/* Navigation Buttons - Floated to the right */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Previous Transaction">
              <span>
                <IconButton
                  onClick={() => handleNavigation(false)}
                  disabled={
                    !selectedYearId ||
                    isNavigating ||
                    navigationLoading ||
                    !navigationData?.previousTransactionId
                  }
                  size="large"
                  sx={{
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "action.disabledBackground",
                      borderColor: "action.disabled",
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Next Transaction">
              <span>
                <IconButton
                  onClick={() => handleNavigation(true)}
                  disabled={
                    !selectedYearId ||
                    isNavigating ||
                    navigationLoading ||
                    !navigationData?.nextTransactionId
                  }
                  size="large"
                  sx={{
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "action.disabledBackground",
                      borderColor: "action.disabled",
                    },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Card elevation={1}>
          <CardContent sx={{ pt: 3, pb: 4 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="transaction tabs"
              >
                <Tab label="Main Info" value="main" />
                <Tab label="Financials" value="financials" />
              </Tabs>
            </Box>

            {activeTab === "main" && (
              <>
                <Typography
                  variant="h6"
                  color="primary"
                  gutterBottom
                  sx={{ mb: 3 }}
                >
                  Transaction Information
                </Typography>

                {isOverdue && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    The Potential Closing Date is in the past. Please update the
                    Potential Closing Date or change the Status to Closed.
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <InfoField
                      label="Status"
                      value={transactionStatusLabel(status)}
                      icon={<BusinessIcon fontSize="small" />}
                    />

                    <InfoField
                      label="Transaction Type"
                      value={getTransactionTypeName()}
                      icon={<BusinessIcon fontSize="small" />}
                    />

                    <InfoField
                      label="Lead Received Date"
                      value={
                        leadReceivedDate
                          ? formatDate(leadReceivedDate.toString())
                          : ""
                      }
                      icon={<CalendarTodayIcon fontSize="small" />}
                    />

                    <InfoField
                      label={closingDateLabel(status)}
                      value={
                        transaction?.date
                          ? formatDate(transaction.date.toString())
                          : ""
                      }
                      icon={<CalendarTodayIcon fontSize="small" />}
                    />

                    <InfoField
                      label="Price"
                      value={
                        transaction?.price
                          ? `$${transaction.price.toLocaleString()}`
                          : ""
                      }
                      icon={<AttachMoneyIcon fontSize="small" />}
                    />

                    <InfoField
                      label="Address"
                      value={transaction?.address?.formattedAddress ?? ""}
                      icon={<LocationOnIcon fontSize="small" />}
                    />

                    <InfoField
                      label="County"
                      value={transaction?.address?.county || ""}
                      icon={<LocationOnIcon fontSize="small" />}
                    />

                    {!hiddenFields.find((x) => x.name === "customArea") && (
                      <InfoField
                        label="Custom Area"
                        value={areaName || ""}
                        icon={<LocationOnIcon fontSize="small" />}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {!hiddenFields.find((x) => x.name === "source") && (
                      <InfoField
                        label="Source"
                        value={getSourceName()}
                        icon={<BusinessIcon fontSize="small" />}
                      />
                    )}

                    {hiddenFields?.find((x) => x.name === "reason") ===
                      undefined && (
                      <InfoField
                        label="Reason"
                        value={getReasonName()}
                        icon={<BusinessIcon fontSize="small" />}
                      />
                    )}

                    {!hiddenFields.find((x) => x.name === "coAgent") && (
                      <InfoField
                        label="Team Member/Co-Agent"
                        value={getCoAgentName()}
                        icon={<PersonIcon fontSize="small" />}
                      />
                    )}

                    {!hiddenFields.find((x) => x.name === "coAgent2") && (
                      <InfoField
                        label="Team Member/Co-Agent 2"
                        value={getCoAgent2Name()}
                        icon={<PersonIcon fontSize="small" />}
                      />
                    )}

                    <InfoField
                      label="Clients"
                      value={transaction?.clients || []}
                      icon={<GroupIcon fontSize="small" />}
                      chip
                    />

                    {!hiddenFields.find((x) => x.name === "vendors") && (
                      <InfoField
                        label="Vendors"
                        value={transaction?.vendors || []}
                        icon={<BusinessIcon fontSize="small" />}
                        chip
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    {!hiddenFields.find((x) => x.name === "specialGroups") && (
                      <InfoField
                        label="Special Groups"
                        value={getSpecialGroups()}
                        icon={<TagIcon fontSize="small" />}
                        chip
                      />
                    )}

                    {!hiddenFields.find((x) => x.name === "specialTags") && (
                      <InfoField
                        label="Special Tags"
                        value={getSpecialTags()}
                        icon={<TagIcon fontSize="small" />}
                        chip
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <InfoField
                      label="Notes"
                      value={transaction?.notes}
                      icon={<NoteIcon fontSize="small" />}
                    />
                  </Grid>
                </Grid>

                <>
                  <Divider sx={{ my: 4 }} />
                  <Typography
                    variant="h6"
                    color="primary"
                    gutterBottom
                    sx={{ mb: 3 }}
                  >
                    Referrers
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <InfoField
                        label="SOI Referrers"
                        value={transaction?.soiReferrers}
                        icon={<PersonIcon fontSize="small" />}
                        chip
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <InfoField
                        label="Agent Referrers"
                        value={transaction?.agentReferrers}
                        icon={<PersonIcon fontSize="small" />}
                        chip
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <InfoField
                        label="Vendor Referrers"
                        value={transaction?.vendorReferrers}
                        icon={<BusinessIcon fontSize="small" />}
                        chip
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <InfoField
                        label="Agent Referred"
                        value={transaction?.agentsReferred}
                        icon={<BusinessIcon fontSize="small" />}
                        chip
                      />
                    </Grid>
                    {getIsReferralOut() && (
                      <Grid item xs={12} md={4}>
                        <InfoField
                          label="Referral Out Type"
                          value={getReferralOutTypeName()}
                          icon={<BusinessIcon fontSize="small" />}
                        />
                      </Grid>
                    )}
                  </Grid>
                </>
              </>
            )}

            {activeTab === "financials" &&
              transaction &&
              (() => {
                const price = transaction?.price || 0;
                const commissionPercentage =
                  transaction.financials?.commissionPercentage || 0;
                const clientRebatePercentage =
                  transaction.financials?.clientRebatePercentage || 0;
                const clientRebateGciPercentage =
                  transaction.financials?.clientRebateGciPercentage || 0;
                const clientRebateFlatAmount =
                  transaction.financials?.clientRebateFlatAmount || 0;
                const referralFeePaidPercentage =
                  transaction.financials?.referralFeePaidPercentage || 0;
                const referralFeeReceivedPercentage =
                  transaction.financials?.referralFeeReceivedPercentage || 0;
                const coAgentSplitPaidPercentage =
                  transaction.financials?.coAgentSplitPaidPercentage || 0;
                const coAgent2SplitPaidPercentage =
                  transaction.financials?.coAgentSplit2PaidPercentage || 0;
                const brokerageSplitPercentage =
                  transaction.financials?.brokerageSplitPercentage || 0;
                const brokerageAdditionalChargePercentage =
                  transaction.financials?.brokerageAdditionalChargePercentage ||
                  0;
                const brokerageFlatFees =
                  transaction.financials?.brokerageFlatFees || 0;
                const flatCommissionFee =
                  transaction.financials?.flatCommissionAmount || 0;
                const incentivesAmount =
                  transaction.financials?.incentivesAmount || 0;

                // Get transaction type information
                const isReferralOut = getIsReferralOut();

                // Calculate derived values
                const grossCommissionIncome =
                  price && commissionPercentage
                    ? price * (commissionPercentage / 100)
                    : 0;

                const totalClientRebate =
                  ((clientRebatePercentage || 0) * price) / 100 +
                  ((clientRebateGciPercentage || 0) * grossCommissionIncome) /
                    100 +
                  (clientRebateFlatAmount || 0);

                const referralFeePaidAmount =
                  ((!isReferralOut ? referralFeePaidPercentage || 0 : 0) *
                    (grossCommissionIncome - totalClientRebate)) /
                  100;

                const referralFeeReceivedAmount =
                  ((isReferralOut ? referralFeeReceivedPercentage || 0 : 0) /
                    100) *
                  (grossCommissionIncome - totalClientRebate);

                // Calculate base amount for co-agent splits (matches SQL CalculateEffectiveGCI logic)
                const baseForCoAgentSplits = isReferralOut
                  ? referralFeeReceivedAmount
                  : grossCommissionIncome -
                    totalClientRebate -
                    referralFeePaidAmount +
                    referralFeeReceivedAmount;

                const coAgentFee =
                  ((coAgentSplitPaidPercentage || 0) / 100) *
                  baseForCoAgentSplits;

                const coAgent2Fee =
                  ((coAgent2SplitPaidPercentage || 0) / 100) *
                  baseForCoAgentSplits;

                const effectiveGCI = isReferralOut
                  ? referralFeeReceivedAmount - coAgentFee - coAgent2Fee
                  : grossCommissionIncome -
                    totalClientRebate -
                    referralFeePaidAmount +
                    referralFeeReceivedAmount -
                    coAgentFee -
                    coAgent2Fee;

                const brokerageSplitAmount =
                  (effectiveGCI * (brokerageSplitPercentage || 0)) / 100;
                const brokerageAdditionalAmount =
                  (effectiveGCI * (brokerageAdditionalChargePercentage || 0)) /
                  100;
                const brokerageTotalFees =
                  brokerageSplitAmount +
                  brokerageAdditionalAmount +
                  (brokerageFlatFees || 0);

                const netCommissionIncome =
                  effectiveGCI - brokerageTotalFees + (flatCommissionFee || 0);

                const netIncome = netCommissionIncome - (incentivesAmount || 0);

                const netIncomePercentage = price
                  ? (netIncome / price) * 100
                  : 0;

                return (
                  <>
                    {/* Transaction Information Header */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="primary"
                        gutterBottom
                        sx={{ mb: 2 }}
                      >
                        Transaction Information
                      </Typography>

                      <Grid container spacing={2}>
                        {/* Address */}
                        <Grid item xs={12} md={4}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <Box sx={{ color: "primary.main", mt: 0.5 }}>
                              <LocationOnIcon fontSize="small" />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                Address
                              </Typography>
                              <Typography variant="body1">
                                {transaction?.address?.formattedAddress ||
                                  "N/A"}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Settlement Date */}
                        <Grid item xs={12} md={4}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <Box sx={{ color: "primary.main", mt: 0.5 }}>
                              <CalendarTodayIcon fontSize="small" />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                Settlement Date
                              </Typography>
                              <Typography variant="body1">
                                {transaction?.date
                                  ? formatDate(transaction.date.toString())
                                  : "N/A"}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Clients */}
                        <Grid item xs={12} md={4}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <Box sx={{ color: "primary.main", mt: 0.5 }}>
                              <GroupIcon fontSize="small" />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                Clients
                              </Typography>
                              <Box
                                sx={{
                                  mt: 0.5,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {transaction?.clients &&
                                transaction.clients.length > 0 ? (
                                  transaction.clients.map((client, index) => (
                                    <Chip
                                      key={index}
                                      label={client.name || ""}
                                      size="small"
                                      sx={{ mb: 0.5 }}
                                    />
                                  ))
                                ) : (
                                  <Typography variant="body1">N/A</Typography>
                                )}
                              </Box>
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Vendors */}
                        {!hiddenFields.find((x) => x.name === "vendors") && (
                          <Grid item xs={12} md={4}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="flex-start"
                            >
                              <Box sx={{ color: "primary.main", mt: 0.5 }}>
                                <BusinessIcon fontSize="small" />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  Vendors
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {transaction?.vendors &&
                                  transaction.vendors.length > 0 ? (
                                    transaction.vendors.map((vendor, index) => (
                                      <Chip
                                        key={index}
                                        label={vendor.name || ""}
                                        size="small"
                                        sx={{ mb: 0.5 }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body1">N/A</Typography>
                                  )}
                                </Box>
                              </Box>
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    <Typography
                      variant="h6"
                      color="primary"
                      gutterBottom
                      sx={{ mb: 3 }}
                    >
                      Financial Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Core Transaction
                        </Typography>
                      </Grid>

                      <FinancialField
                        label="Commission Percentage"
                        value={commissionPercentage}
                        percentage
                      />
                      <FinancialField
                        label="Gross Commission Income"
                        value={grossCommissionIncome}
                      />
                      <FinancialField
                        label="Flat Commission"
                        value={flatCommissionFee}
                      />

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Client Rebate
                        </Typography>
                      </Grid>

                      <FinancialField
                        label="Client Rebate (% of Price)"
                        value={clientRebatePercentage}
                        percentage
                      />
                      <FinancialField
                        label="Client Rebate (% of GCI)"
                        value={clientRebateGciPercentage}
                        percentage
                      />
                      <FinancialField
                        label="Client Rebate (Flat Amount)"
                        value={clientRebateFlatAmount}
                      />
                      <FinancialField
                        label="Total Client Rebate"
                        value={totalClientRebate}
                      />

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Referral and Co-Agent Fees
                        </Typography>
                      </Grid>

                      {!isReferralOut && (
                        <FinancialField
                          label="Referral Fee Paid"
                          value={referralFeePaidPercentage}
                          percentage
                        />
                      )}
                      {!isReferralOut && (
                        <FinancialField
                          label="Referral Fee Paid Amount"
                          value={referralFeePaidAmount}
                        />
                      )}
                      {isReferralOut && (
                        <FinancialField
                          label="Referral Fee Received"
                          value={referralFeeReceivedPercentage}
                          percentage
                        />
                      )}
                      {isReferralOut && (
                        <FinancialField
                          label="Referral Fee Received Amount"
                          value={referralFeeReceivedAmount}
                        />
                      )}
                      <FinancialField
                        label="Team Member/Co-Agent Split Paid"
                        value={coAgentSplitPaidPercentage}
                        percentage
                      />
                      <FinancialField
                        label="Team Member/Co-Agent Split Amount"
                        value={coAgentFee}
                      />
                      <FinancialField
                        label="Team Member/Co-Agent 2 Split Paid"
                        value={coAgent2SplitPaidPercentage}
                        percentage
                      />
                      <FinancialField
                        label="Team Member/Co-Agent 2 Split Amount"
                        value={coAgent2Fee}
                      />

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                      <FinancialField
                        label="Effective GCI"
                        value={effectiveGCI}
                      />

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Brokerage Details
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <DateField
                          label="Brokerage Anniversary Date"
                          value={
                            transaction.financials?.brokerageAnniversaryDate
                          }
                        />
                        {/* <InfoField
                          label="Brokerage Anniversary Date"
                          value={
                            transaction.financials?.brokerageAnniversaryDate
                              ? formatDate(
                                  transaction.financials?.brokerageAnniversaryDate?.toString()
                                )
                              : "N/A"
                          }
                          icon={<CalendarTodayIcon fontSize="small" />}
                        /> */}
                      </Grid>
                      <FinancialField
                        label="Brokerage Split"
                        value={brokerageSplitPercentage}
                        percentage
                      />
                      <FinancialField
                        label="Brokerage Split Amount"
                        value={brokerageSplitAmount}
                      />
                      <FinancialField
                        label="Total Brokerage Split Paid Anniversary-To-Date"
                        value={transaction?.financials?.runningSplitTotal?.toFixed(
                          2,
                        )}
                      />

                      <FinancialField
                        label="Brokerage Additional Charge"
                        value={brokerageAdditionalChargePercentage}
                        percentage
                      />
                      <FinancialField
                        label="Brokerage Additional Charge Amount"
                        value={brokerageAdditionalAmount}
                      />
                      <FinancialField
                        label="Total Additional Brokerage Charges Paid Anniversary-To-Date"
                        value={transaction?.financials?.runningAdditionalChargeTotal?.toFixed(
                          2,
                        )}
                      />
                      <FinancialField
                        label="Brokerage Flat Fees"
                        value={brokerageFlatFees}
                      />
                      <FinancialField
                        label="Brokerage Total Fees"
                        value={brokerageTotalFees}
                      />

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Net Calculations
                        </Typography>
                      </Grid>

                      <FinancialField
                        label="Net Commission Income"
                        value={netCommissionIncome}
                      />

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Other
                        </Typography>
                      </Grid>

                      <FinancialField
                        label="Expenses Paid Out of Pocket"
                        value={incentivesAmount}
                      />
                      <FinancialField label="Net Income" value={netIncome} />
                      <FinancialField
                        label="Net Income %"
                        value={netIncomePercentage}
                        percentage
                      />
                    </Grid>
                  </>
                );
              })()}
          </CardContent>
        </Card>
      </Box>
    </PageLayout>
  );
}
